import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, formatDate, formatRelativeTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Clock,
  Package,
  CheckCircle,
  XCircle,
  Loader2,
  User,
  Ticket,
  AlertTriangle,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const requestTypeConfig = {
  tiempo: { label: 'Extensión de Tiempo', icon: Clock, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  recurso: { label: 'Solicitud de Recursos', icon: Package, color: 'bg-purple-100 text-purple-700 border-purple-200' },
};

const requestStatusConfig = {
  pendiente: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  aprobada: { label: 'Aprobada', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  rechazada: { label: 'Rechazada', color: 'bg-rose-100 text-rose-700 border-rose-200' },
};

export default function SolicitudesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewComment, setReviewComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pendientes');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/extensions/pending`),
        axios.get(`${API_URL}/extensions/all`),
      ]);
      setPendingRequests(pendingRes.data);
      setAllRequests(allRes.data);
    } catch (error) {
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (status) => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      await axios.patch(`${API_URL}/extensions/${selectedRequest.id}/review`, {
        status,
        reviewer_comment: reviewComment || null,
      });
      toast.success(status === 'aprobada' ? 'Solicitud aprobada' : 'Solicitud rechazada');
      setReviewDialogOpen(false);
      setReviewComment('');
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al revisar solicitud');
    } finally {
      setActionLoading(false);
    }
  };

  const openReviewDialog = (request) => {
    setSelectedRequest(request);
    setReviewComment('');
    setReviewDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="solicitudes-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#7FD02E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="solicitudes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-gray-800">
            Solicitudes
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestión de extensiones de tiempo y solicitudes de recursos
          </p>
        </div>
        {pendingRequests.length > 0 && (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-sm px-3 py-1 self-start" data-testid="pending-count-badge">
            <AlertTriangle className="w-4 h-4 mr-1" />
            {pendingRequests.length} pendiente{pendingRequests.length > 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100" data-testid="solicitudes-tabs">
          <TabsTrigger value="pendientes" className="data-[state=active]:bg-white" data-testid="tab-pendientes">
            Pendientes ({pendingRequests.length})
          </TabsTrigger>
          <TabsTrigger value="todas" className="data-[state=active]:bg-white" data-testid="tab-todas">
            Todas ({allRequests.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pendientes" className="mt-4">
          {pendingRequests.length === 0 ? (
            <Card className="border-gray-200 bg-white shadow-sm">
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                <p className="text-gray-500 text-lg">No hay solicitudes pendientes</p>
                <p className="text-gray-400 text-sm mt-1">Todas las solicitudes han sido revisadas</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingRequests.map((req) => (
                <RequestCard
                  key={req.id}
                  request={req}
                  onReview={() => openReviewDialog(req)}
                  onNavigate={() => navigate(`/tickets/${req.ticket_id}`)}
                  showActions
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="todas" className="mt-4">
          {allRequests.length === 0 ? (
            <Card className="border-gray-200 bg-white shadow-sm">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-lg">No hay solicitudes</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {allRequests.map((req) => (
                <RequestCard
                  key={req.id}
                  request={req}
                  onNavigate={() => navigate(`/tickets/${req.ticket_id}`)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Revisar Solicitud</DialogTitle>
            <DialogDescription>
              {selectedRequest && (
                <>
                  {requestTypeConfig[selectedRequest.request_type]?.label} de{' '}
                  <strong>{selectedRequest.requester?.first_name} {selectedRequest.requester?.last_name}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg bg-gray-50 p-4 space-y-2">
                <p className="text-sm font-medium text-gray-700">Motivo:</p>
                <p className="text-sm text-gray-600">{selectedRequest.reason}</p>
                {selectedRequest.requested_hours && (
                  <p className="text-sm text-gray-600">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Horas solicitadas: <strong>{selectedRequest.requested_hours}h</strong>
                  </p>
                )}
                {selectedRequest.resource_description && (
                  <p className="text-sm text-gray-600">
                    <Package className="w-3 h-3 inline mr-1" />
                    Recursos: {selectedRequest.resource_description}
                  </p>
                )}
              </div>
              <Textarea
                placeholder="Comentario de revisión (opcional)..."
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                rows={3}
                data-testid="review-comment-input"
              />
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => handleReview('rechazada')}
              disabled={actionLoading}
              className="border-rose-200 text-rose-700 hover:bg-rose-50"
              data-testid="reject-btn"
            >
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <XCircle className="w-4 h-4 mr-1" />
              Rechazar
            </Button>
            <Button
              onClick={() => handleReview('aprobada')}
              disabled={actionLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="approve-btn"
            >
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <CheckCircle className="w-4 h-4 mr-1" />
              Aprobar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RequestCard({ request, onReview, onNavigate, showActions }) {
  const TypeIcon = requestTypeConfig[request.request_type]?.icon || FileText;

  return (
    <Card className="border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid={`request-card-${request.id}`}>
      <CardContent className="p-5">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Icon */}
          <div className="flex-shrink-0">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              request.request_type === 'tiempo' ? 'bg-blue-100' : 'bg-purple-100'
            }`}>
              <TypeIcon className={`w-5 h-5 ${
                request.request_type === 'tiempo' ? 'text-blue-600' : 'text-purple-600'
              }`} />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={requestTypeConfig[request.request_type]?.color}>
                {requestTypeConfig[request.request_type]?.label}
              </Badge>
              <Badge className={requestStatusConfig[request.status]?.color}>
                {requestStatusConfig[request.status]?.label}
              </Badge>
              {request.requested_hours && (
                <span className="text-xs text-gray-500">
                  <Clock className="w-3 h-3 inline mr-0.5" />{request.requested_hours}h
                </span>
              )}
            </div>

            <p className="text-sm text-gray-700">{request.reason}</p>

            {request.resource_description && (
              <p className="text-sm text-gray-500">
                <Package className="w-3 h-3 inline mr-1" />
                {request.resource_description}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400">
              {request.requester && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {request.requester.first_name} {request.requester.last_name}
                </span>
              )}
              {request.ticket && (
                <button
                  onClick={onNavigate}
                  className="flex items-center gap-1 hover:text-[#7FD02E] transition-colors"
                  data-testid={`go-to-ticket-${request.id}`}
                >
                  <Ticket className="w-3 h-3" />
                  {request.ticket.ticket_number}
                </button>
              )}
              <span>{formatRelativeTime(request.created_at)}</span>
            </div>

            {request.reviewer_comment && (
              <div className="mt-2 p-2 rounded bg-gray-50 border border-gray-100">
                <p className="text-xs text-gray-500">
                  <strong>{request.reviewer?.first_name}:</strong> {request.reviewer_comment}
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          {showActions && (
            <div className="flex-shrink-0 self-center">
              <Button
                onClick={onReview}
                size="sm"
                className="bg-[#7FD02E] hover:bg-[#6AB825] text-white"
                data-testid={`review-btn-${request.id}`}
              >
                Revisar
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
